import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import {
  LeaderboardTable,
  LeaderboardTableQueryOptions,
  LeaderboardTopPerformerCards,
  LeaderboardViewControls,
  TrophyCase,
  TrophyCaseTable,
} from "@/components/leaderboard";
import { Button } from "@/components/ui/button";
import {
  useInactiveUserIds,
  useTaggableUsers,
} from "@/contexts/TaggableUsersContext";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetAllUserSettings } from "@/hooks/queries/useGetAllUserSettings";
import { useGetLeaderboard } from "@/hooks/queries/useGetLeaderboard";
import { defaultLeaderboardQueryParams } from "@/lib/constants";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { LeaderboardQueryParams, UserRole } from "@/types";

enum LeaderboardView {
  LEADERBOARD = "leaderboard",
  TROPHY_CASE = "trophy-case",
}

export const LeaderboardPage = () => {
  const [queryParams, setQueryParams] = useState<LeaderboardQueryParams>(
    defaultLeaderboardQueryParams,
  );
  const [showAllStaffers, setShowAllStaffers] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { users: taggableUsers } = useTaggableUsers();
  const view =
    searchParams.get("view") === LeaderboardView.TROPHY_CASE
      ? LeaderboardView.TROPHY_CASE
      : LeaderboardView.LEADERBOARD;

  /* React Hooks */
  useSetTitle({ title: "Leaderboard" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.LEADERBOARD);
  }, []);

  const {
    data,
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
  } = useGetLeaderboard(queryParams, { isRefetchedOnMount: true });
  const {
    data: racetrackData,
    isError: isRacetrackError,
    isLoading: isRacetrackLoading,
  } = useGetAllUserSettings();

  // Actual staffers
  const stafferIds = useMemo(
    () =>
      new Set(
        taggableUsers
          .filter(
            (u) => u.userRole === UserRole.STAFFER && u.deletedAt === null,
          )
          .map((u) => u.id),
      ),
    [taggableUsers],
  );

  // Deactivated staffers (e.g. no longer employed) only ever show up if they
  // produced value (submits/interviews/offers/placements/revenue) in the period
  const inactiveUserIds = useInactiveUserIds();

  /* Handlers */
  const handleToggleView = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (view === LeaderboardView.TROPHY_CASE) {
      nextSearchParams.delete("view");
    } else {
      nextSearchParams.set("view", LeaderboardView.TROPHY_CASE);
    }
    setSearchParams(nextSearchParams);
  };

  /* Helpers */
  const viewControls = (
    <LeaderboardViewControls
      isTrophyCase={view === LeaderboardView.TROPHY_CASE}
      onToggleView={handleToggleView}
    />
  );
  // The controls ride inside the racetrack card when it's shown; otherwise they
  // float over the page. Hidden while the preference loads so they don't jump.
  const hasFloatingControls =
    view === LeaderboardView.TROPHY_CASE ||
    (!isRacetrackLoading && racetrackData?.showRacetrack !== true);

  /* Render */
  // Wait on the racetrack show preference too, so the racetrack doesn't flash in
  // before the setting settles.
  const renderContent = () => {
    if (view === LeaderboardView.TROPHY_CASE) {
      if (isLeaderboardLoading || isRacetrackLoading) {
        return <LoadingIndicator variant="medium" />;
      }
      if (isLeaderboardError || isRacetrackError || !data || !racetrackData) {
        return <ErrorText errorMessage="Unable to load leaderboard data." />;
      }
      const visibleUsers = data.filter((user) => stafferIds.has(user.userId));
      return (
        <Column className="gap-10 px-6 pb-12 pt-20">
          <TrophyCase queryParams={queryParams} />
          <TrophyCaseTable leaderboardUsers={visibleUsers} />
        </Column>
      );
    }
    if (isRacetrackLoading || isLeaderboardLoading) {
      return <LoadingIndicator variant="medium" />;
    }
    if (isRacetrackError || isLeaderboardError || !racetrackData || !data) {
      return <ErrorText errorMessage="Unable to load leaderboard data." />;
    }

    const visibleUsers = data.filter(
      (u) =>
        !inactiveUserIds.has(u.userId) || leaderboardMetrics.hasActivity(u),
    );
    const tableUsers = showAllStaffers
      ? visibleUsers
      : visibleUsers.filter(
          (u) => stafferIds.has(u.userId) || leaderboardMetrics.hasActivity(u),
        );

    return (
      <Column className="gap-10 pb-12">
        <LeaderboardTopPerformerCards
          users={data}
          queryParams={queryParams}
          showRacetrack={racetrackData.showRacetrack}
          controls={viewControls}
        />
        <Column className="gap-4 px-6">
          <LeaderboardTableQueryOptions
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
          <LeaderboardTable users={tableUsers} queryParams={queryParams} />
          <Row className="justify-end">
            <Button
              variant="ghost"
              className="h-auto px-2 py-1 text-xs font-normal text-slate-400 hover:text-slate-600"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  showAllStaffers ? "Show staffers only" : "See all",
                  MIXPANEL_SCREENS.LEADERBOARD,
                );
                setShowAllStaffers((v) => !v);
              }}
            >
              {showAllStaffers ? "Show staffers only" : "See all"}
            </Button>
          </Row>
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className="relative h-svh min-h-0">
        {hasFloatingControls ? viewControls : null}
        {/* Opaque background lets Chrome GPU-scroll this on low-DPI screens;
            without it, it repaints every scroll frame to keep LCD text. */}
        <Column className="min-h-0 flex-1 overflow-y-auto bg-white">
          {renderContent()}
        </Column>
      </Column>
    );
  };

  return render();
};
